import { DocumentType, getOrderTypeTranslationId } from '@synergy/models';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloColumn, PaloFilterChips, PaloListView, PaloRowAction, PaloStatusPill } from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { useNavigate } from 'react-router';

import { PaloContactName } from '../../../../common/components/palo/palo-contact-card';
import { ContactPresenter } from '../../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../../convert-receipt/converter-receipt-modal';
import { SalesOrder } from '../../../sales-orders/models/salesOrder';
import { SalesOrderTransition } from '../../../convert-receipt/models/sales-order/salesOrdersTransition';
import { Transition } from '../../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../../convert-receipt/models/internal/transition-target';
import {
    deleteSalesOrder,
    fetchSalesOrder,
    requestEnd as salesOrderRequestEnd,
    resetSalesOrderState,
} from '../../../sales-orders/sales-order-actions';
import { fetchTransitions, resetTransitions } from '../../../../common/actions/transitions-activity-actions';
import { requestingSelector } from '../../store/dashboard-selectors';
import {
    transitionSalesOrderIntoInquiries,
    transitionSalesOrderIntoPurchaseOrders,
} from '../../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useDashboardSort } from '../../palo/use-dashboard-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const columns: PaloColumn<SalesOrder>[] = [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.numberAbbreviation" />,
        cell: order => order.number,
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: order => (
            <PaloStatusPill
                status={order.status}
                label={<FormattedMessage id={getDocumentStatusTranslationId(order.status)} />}
            />
        ),
    },
    {
        key: 'type',
        type: 'classification',
        sortKey: 'type',
        head: <FormattedMessage id="app.table.column.type" />,
        cell: order => <FormattedMessage id={getOrderTypeTranslationId(order.type)} />,
    },
    {
        key: 'customer',
        type: 'name',
        sortKey: 'customerAccount',
        head: <FormattedMessage id="app.table.column.customer" />,
        cell: order => <PaloContactName company={order.customerAccount} kind="customer" />,
    },
    {
        key: 'customerContact',
        type: 'name',
        sortKey: 'customerContact',
        head: <FormattedMessage id="app.table.column.customerContact" />,
        cell: order => (
            <ContactPresenter
                person={order.customerContact}
                fullName={order.customerContact?.fullName}
                isDeleted={order.customerContact?.isDeleted}
                isLocked={order.customerContact?.isLocked}
            />
        ),
    },
    {
        key: 'responsibleContact',
        type: 'name',
        sortKey: 'responsibleContact',
        head: <FormattedMessage id="app.common.responsibleContact" />,
        cell: order => (
            <ContactPresenter
                person={order.responsibleContact}
                fullName={order.responsibleContact?.fullName}
                isDeleted={order.responsibleContact?.isDeleted}
                isLocked={order.responsibleContact?.isLocked}
            />
        ),
    },
    {
        key: 'createdAt',
        type: 'date',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: order => <FormattedDate value={order.createdAt} year="numeric" month="short" day="2-digit" />,
    },
];

export const DashboardOverviewSalesOrders = (): ReactElement => {
    const navigate = useNavigate();
    const { sort, onSortChange } = useDashboardSort('sales-orders');
    const dispatch = useThunkDispatch();
    const [isConverterVisible, setIsConverterVisible] = useState(false);
    const salesOrder = useAppSelector(state => state.salesOrders.entity);
    const salesOrders = useAppSelector(state => state.salesOrders.flat);
    const requesting = useAppSelector(requestingSelector);

    const cleanupState = (): void => {
        dispatch(resetSalesOrderState());
        dispatch(salesOrderRequestEnd());
    };

    useEffect(() => {
        return cleanupState;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConvertOpen = (id: number): void => {
        dispatch(resetSalesOrderState());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.SalesOrder, id));
        dispatch(fetchSalesOrder(id));
        setIsConverterVisible(true);
    };

    const handleTransition = (transitionObject: Transition, target: TransitionTarget) =>
        TransitionTarget.PurchaseOrder === target
            ? dispatch(transitionSalesOrderIntoPurchaseOrders(transitionObject as SalesOrderTransition))
            : dispatch(transitionSalesOrderIntoInquiries(transitionObject as SalesOrderTransition));

    const handleConvertClose = (): void => {
        cleanupState();
        setIsConverterVisible(false);
    };

    const rowActions = (order: SalesOrder): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => navigate('/sales-orders/' + order.id) },
        { label: 'Umwandeln', onClick: () => handleConvertOpen(order.id) },
        { label: 'Löschen', onClick: () => dispatch(deleteSalesOrder(order.id)) },
    ];

    return (
        <>
            <PoolDocumentTitle titleTranslationId="app.navigation.dashboard.salesOrders" />
            <PaloFilterChips />
            <PaloListView<SalesOrder>
                embedded
                columns={columns}
                sort={sort}
                onSortChange={onSortChange}
                items={salesOrders.items}
                getRowId={order => order.id}
                requesting={requesting}
                emptyLabel="Keine Aufträge."
                onRowClick={order => navigate('/sales-orders/' + order.id)}
                rowActions={rowActions}
            />
            {salesOrder && (
                <ConverterReceiptModal
                    isVisible={isConverterVisible}
                    source={salesOrder}
                    onClose={handleConvertClose}
                    onConvert={handleTransition}
                />
            )}
        </>
    );
};
